import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import CreateOrEditAssetLibraryModal from '@modals/CreateOrEditAssetLibraryModal'
import CreateOrEditAttachmentModal from '@modals/CreateOrEditAttachmentModal'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { AssetLibraryModel, AttachmentModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditAssetLibraryModal: false,
  showCreateOrEditAttachmentModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditAssetLibraryModal: {
      closeModal: () => setState({ showCreateOrEditAssetLibraryModal: false }),
      createAssetLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAssetLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAssetLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditAttachmentModal: {
      closeModal: () => setState({ showCreateOrEditAttachmentModal: false }),
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const AssetLibraryList = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditAssetLibraryModal, showCreateOrEditAttachmentModal } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditAssetLibraryModal: (payload: AssetLibraryModel) => {
        setModalData('CreateOrEditAssetLibraryModal', payload)
        setState({ showCreateOrEditAssetLibraryModal: true })
      },
      showCreateOrEditAttachmentModal: (payload: AttachmentModel) => {
        setModalData('CreateOrEditAttachmentModal', payload)
        setState({ showCreateOrEditAttachmentModal: true })
      },
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditAssetLibraryModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAssetLibraryModal', setState)}
          showModal={showCreateOrEditAssetLibraryModal}
        />

        <CreateOrEditAttachmentModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAttachmentModal', setState)}
          showModal={showCreateOrEditAttachmentModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default AssetLibraryList
